import { create } from 'zustand';
import { devtools, persist, createJSONStorage, StateStorage } from 'zustand/middleware';
import type {
  SetupType,
  ViewType,
} from '@/features/plants/components/shared/plants-list-view-filter-command.tsx';

const LayoutTypeLiteral = ['grid', 'table'] as const;
type LayoutType = (typeof LayoutTypeLiteral)[number];

interface PlantsViewFilterState {
  view: ViewType;
  setupType: SetupType;
  layout: LayoutType;
  showArchived: boolean;
}

interface PlantsViewFilterStore extends PlantsViewFilterState {
  setView: (view: ViewType) => void;
  setSetupType: (setupType: SetupType) => void;
  setLayout: (layout: LayoutType) => void;
  setShowArchived: (showArchived: boolean) => void;
  setFilterState: (state: Partial<PlantsViewFilterState>) => void;
}

const defaultState: PlantsViewFilterState = {
  view: 'overview',
  setupType: 'all',
  layout: 'grid',
  showArchived: false,
};

const urlParamKeys = {
  view: 'view',
  setupType: 'type',
  layout: 'layout',
  showArchived: 'archived',
} as const;

const urlStorage: StateStorage = {
  getItem: (): string => {
    if (typeof window === 'undefined') return JSON.stringify({ state: defaultState });

    const searchParams = new URLSearchParams(window.location.search);
    const state: PlantsViewFilterState = {
      view: (searchParams.get(urlParamKeys.view) as ViewType) || defaultState.view,
      setupType: (searchParams.get(urlParamKeys.setupType) as SetupType) || defaultState.setupType,
      layout: (searchParams.get(urlParamKeys.layout) as LayoutType) || defaultState.layout,
      showArchived: searchParams.get(urlParamKeys.showArchived) === 'true',
    };

    return JSON.stringify({ state });
  },

  setItem: (_, newValue): void => {
    if (typeof window === 'undefined') return;

    const { state } = JSON.parse(newValue) as { state: PlantsViewFilterState };
    const searchParams = new URLSearchParams(window.location.search);

    if (state.view !== defaultState.view) {
      searchParams.set(urlParamKeys.view, state.view);
    } else {
      searchParams.delete(urlParamKeys.view);
    }

    if (state.setupType !== defaultState.setupType) {
      searchParams.set(urlParamKeys.setupType, state.setupType);
    } else {
      searchParams.delete(urlParamKeys.setupType);
    }

    if (state.layout !== defaultState.layout) {
      searchParams.set(urlParamKeys.layout, state.layout);
    } else {
      searchParams.delete(urlParamKeys.layout);
    }

    if (state.showArchived !== defaultState.showArchived) {
      searchParams.set(urlParamKeys.showArchived, String(state.showArchived));
    } else {
      searchParams.delete(urlParamKeys.showArchived);
    }

    const newUrl = searchParams.toString()
      ? `${window.location.pathname}?${searchParams.toString()}`
      : window.location.pathname;

    window.history.replaceState(null, '', newUrl);
  },

  removeItem: (): void => {
    if (typeof window === 'undefined') return;

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete(urlParamKeys.view);
    searchParams.delete(urlParamKeys.setupType);
    searchParams.delete(urlParamKeys.layout);
    searchParams.delete(urlParamKeys.showArchived);

    const newUrl = searchParams.toString()
      ? `${window.location.pathname}?${searchParams.toString()}`
      : window.location.pathname;

    window.history.replaceState(null, '', newUrl);
  },
};

export const usePlantsViewFilterStore = create<PlantsViewFilterStore>()(
  devtools(
    persist(
      (set) => ({
        ...defaultState,

        setView: (view) => set({ view }),

        setSetupType: (setupType) => set({ setupType }),

        setLayout: (layout) => set({ layout }),

        setShowArchived: (showArchived) => set({ showArchived }),

        setFilterState: (state) => set((prev) => ({ ...prev, ...state })),
      }),
      {
        name: 'plants-view-filter',
        storage: createJSONStorage(() => urlStorage),
      }
    ),
    { name: 'ems-plants-view-filter-store' }
  )
);
